// Repair a project read from a file or from storage: wrong types and out-of-range numbers
// take the default, unknown keys are dropped, invalid planes and windows are removed.
// Never throws on content; the caller decides what a non-project is. GPL-3.0-or-later.
import type { SetWindow, Traverse, Vec3 } from "@dipfield/stereo";
import { PROJECT_FORMAT, PROJECT_VERSION, UNITS, type JointSet, type Material, type Plane, type Project, type TraverseDef } from "./schema.js";
import { defaultData, defaultSlope, defaultStereo, defaultTunnel, defaultReport, material, newId } from "./defaults.js";

type Any = any; // eslint-disable-line @typescript-eslint/no-explicit-any
export const isObj = (v: unknown): v is Record<string, Any> => !!v && typeof v === "object" && !Array.isArray(v);
export const num = (v: unknown, def: number, lo = -Infinity, hi = Infinity) => {
  const x = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isFinite(x) ? Math.min(hi, Math.max(lo, x)) : def;
};
export const numOrNull = (v: unknown, lo = -Infinity, hi = Infinity) => (v == null || v === "" ? null : num(v, NaN, lo, hi)) as number | null;
const nn = (v: number | null) => (v == null || Number.isNaN(v) ? null : v);
export const bool = (v: unknown, def: boolean) => (typeof v === "boolean" ? v : def);
export const oneOf = <T extends string>(v: unknown, opts: readonly T[], def: T): T => (opts.includes(v as T) ? (v as T) : def);
export const str = (v: unknown, def: string) => (typeof v === "string" && v.trim() !== "" ? v : typeof v === "number" ? String(v) : def);
const wrap = (a: number) => ((a % 360) + 360) % 360;

const vec = (v: unknown): Vec3 | null =>
  Array.isArray(v) && v.length === 3 && v.every((x) => typeof x === "number" && Number.isFinite(x)) && Math.hypot(v[0], v[1], v[2]) > 1e-12 ? [v[0], v[1], v[2]] : null;

export function sanitizeWindow(w: unknown): SetWindow | null {
  if (!isObj(w)) return null;
  if (w.kind === "circle") { const a = vec(w.axis); const r = num(w.radius, NaN, 0.1, 90); return a && Number.isFinite(r) ? { kind: "circle", axis: a, radius: r } : null; }
  if (w.kind === "curved") {
    const t0 = num(w.trendStart, NaN), tw = num(w.trendWidth, NaN, 0, 360), lo = num(w.plungeMin, NaN, -90, 90), hi = num(w.plungeMax, NaN, -90, 90);
    return [t0, tw, lo, hi].every(Number.isFinite) && hi >= lo ? { kind: "curved", trendStart: wrap(t0), trendWidth: tw, plungeMin: lo, plungeMax: hi } : null;
  }
  if (w.kind === "freehand") {
    const vs = Array.isArray(w.vertices) ? w.vertices.map(vec) : [];
    if (vs.length < 3 || vs.some((v) => !v)) return null;
    return { kind: "freehand", vertices: vs as Vec3[], method: oneOf(w.method, ["equal-area", "equal-angle"] as const, "equal-area"), hemisphere: oneOf(w.hemisphere, ["lower", "upper"] as const, "lower") };
  }
  return null;
}

export function sanitizeTraverse(t: unknown): Traverse {
  if (isObj(t) && t.kind === "linear") return { kind: "linear", trend: wrap(num(t.trend, 0)), plunge: num(t.plunge, 0, -90, 90) };
  if (isObj(t) && t.kind === "planar") return { kind: "planar", dip: num(t.dip, 90, 0, 90), dipDir: wrap(num(t.dipDir, 0)) };
  return { kind: "spot" };
}

export function sanitizePlanes(v: unknown): Plane[] {
  if (!Array.isArray(v)) return [];
  const out: Plane[] = [];
  for (const p of v) {
    if (!isObj(p)) continue;
    const dip = num(p.dip, NaN), dd = num(p.dipDir, NaN);
    if (!(dip >= 0 && dip <= 90) || !Number.isFinite(dd)) continue;
    const q = num(p.quantity, 1);
    out.push({ dip, dipDir: wrap(dd), quantity: q > 0 ? q : 1, ...(p.traverse != null && p.traverse !== "" ? { traverse: String(p.traverse) } : {}) });
  }
  return out;
}

export function sanitizeMaterial(m: unknown): Material {
  const d = material(); if (!isObj(m)) return d;
  return {
    model: oneOf(m.model, ["mc", "bb", "power"] as const, "mc"),
    c: num(m.c, d.c, 0), phi: num(m.phi, d.phi, 0, 89.9),
    jrc: num(m.jrc, d.jrc, 0, 20), jcs: num(m.jcs, d.jcs, 0), phir: num(m.phir, d.phir, 0, 89.9),
    a: num(m.a, d.a), b: num(m.b, d.b), pc: num(m.pc, d.pc), d: num(m.d, d.d),
    waviness: num(m.waviness, d.waviness, 0, 45), waterPressure: num(m.waterPressure, d.waterPressure, 0), spacing: num(m.spacing, d.spacing, 0),
  };
}

export function sanitizeSets(v: unknown): JointSet[] {
  if (!Array.isArray(v)) return [];
  const out: JointSet[] = [], ids = new Set<string>();
  v.forEach((s, k) => {
    if (!isObj(s)) return;
    const w = sanitizeWindow(s.window); if (!w) return;
    let id = str(s.id, `J${k + 1}`); while (ids.has(id)) id += "'"; ids.add(id);
    out.push({ id, name: str(s.name, `J${k + 1}`), window: w, friction: nn(numOrNull(s.friction, 0, 89)) });
  });
  return out;
}

/** A full v2 project from anything that claims to be one. */
export function sanitize(x: unknown): Project {
  const j: Any = isObj(x) ? x : {};
  const dD = defaultData(), dS = defaultStereo(), dL = defaultSlope(), dT = defaultTunnel();
  const m: Any = isObj(j.meta) ? j.meta : {};
  const now = new Date().toISOString();

  const d: Any = isObj(j.data) ? j.data : {};
  const traverses: TraverseDef[] = Array.isArray(d.traverses)
    ? d.traverses.filter(isObj).map((t: Any, k: number) => ({ id: str(t.id, `T${k + 1}`), name: str(t.name, `T${k + 1}`), tr: sanitizeTraverse(t.tr) }))
    : [];
  const data = {
    format: oneOf(d.format, ["dip-dipdir", "strike-rhr-dip", "strike-lhr-dip", "trend-plunge"] as const, dD.format),
    planes: sanitizePlanes(d.planes),
    traverses: traverses.length ? traverses : dD.traverses,
    terzaghi: { use: bool(d.terzaghi?.use, false), minBiasDeg: num(d.terzaghi?.minBiasDeg, 15, 1, 45) },
  };

  const s: Any = isObj(j.stereo) ? j.stereo : {}, sd: Any = isObj(s.display) ? s.display : {}, sr: Any = isObj(s.rose) ? s.rose : {};
  const se: Any = isObj(s.dse) ? s.dse : {}, sp: Any = isObj(s.projection) ? s.projection : {}, sn: Any = isObj(s.density) ? s.density : {};
  let dipMin = num(sr.dipMin, 0, 0, 90), dipMax = num(sr.dipMax, 90, 0, 90); if (dipMin > dipMax) [dipMin, dipMax] = [dipMax, dipMin];
  const stereo = {
    projection: { method: oneOf(sp.method, ["equal-area", "equal-angle"] as const, "equal-area"), hemisphere: oneOf(sp.hemisphere, ["lower", "upper"] as const, "lower") },
    density: { method: oneOf(sn.method, ["fisher-overlap", "schmidt"] as const, "fisher-overlap"), countCircle: num(sn.countCircle, 0.01, 0.001, 0.1), coneRule: oneOf(sn.coneRule, ["dips", "exact"] as const, "dips") },
    display: {
      view: oneOf(sd.view, ["net", "rose"] as const, "net"), poles: bool(sd.poles, true), poleSize: num(sd.poleSize, 2.5, 1, 5),
      contours: bool(sd.contours, true), contourStyle: oneOf(sd.contourStyle, ["bands", "smooth"] as const, "bands"),
      overlay: oneOf(sd.overlay, ["none", "polar", "equatorial"] as const, "none"),
      globalMean: bool(sd.globalMean, false), bestFit: bool(sd.bestFit, false), setPlanes: bool(sd.setPlanes, true), cones: bool(sd.cones, false),
    },
    rose: {
      mode: oneOf(sr.mode, ["strike", "dipDir"] as const, "strike"), bin: num(sr.bin, 10, 1, 90), dipMin, dipMax,
      scale: oneOf(sr.scale, ["area", "linear"] as const, "area"), set: str(sr.set, "all"), showMean: bool(sr.showMean, true),
      ring: oneOf(sr.ring, ["auto", "fixed"] as const, "auto"), ringPct: num(sr.ringPct, 15, 0.5, 100), overlay: bool(sr.overlay, false),
    },
    dse: {
      maxSets: Math.round(num(se.maxSets, dS.dse.maxSets, 1, 12)), minAngleDeg: num(se.minAngleDeg, 30, 5, 60), coneDeg: num(se.coneDeg, 30, 5, 60),
      minPeakPct: num(se.minPeakPct, 5, 0, 50), projection: oneOf(se.projection, ["Equal-area", "Equal-angle"] as const, "Equal-area"), useWeights: bool(se.useWeights, true),
    },
  };

  const l: Any = isObj(j.slope) ? j.slope : {}, lk: Any = isObj(l.kinematics) ? l.kinematics : {}, lg: Any = isObj(l.geometry) ? l.geometry : {};
  const lr: Any = isObj(l.run) ? l.run : {}, ll: Any = isObj(l.loads) ? l.loads : {}, lc: Any = isObj(l.caps) ? l.caps : {}, lv: Any = isObj(l.view) ? l.view : {};
  const sets = sanitizeSets(j.sets);
  const materials: Record<string, Material> = {};
  if (isObj(l.materials)) for (const [k, v] of Object.entries(l.materials)) materials[k] = sanitizeMaterial(v);
  const cap = (v: unknown) => { const x = nn(numOrNull(v, 0)); return x && x > 0 ? x : null; };
  const slope = {
    face: { dip: num(l.face?.dip, dL.face.dip, 1, 90), dipDir: wrap(num(l.face?.dipDir, dL.face.dipDir)) },
    lateral: num(l.lateral, 20, 5, 45),
    kinematics: {
      enabled: bool(lk.enabled, false), friction: num(lk.friction, 30, 0, 60), noLimits: bool(lk.noLimits, false),
      show: oneOf(lk.show, ["planar", "wedge", "flexural", "direct"] as const, "planar"), pairs: oneOf(lk.pairs, ["all", "sets"] as const, "all"),
      showZone: bool(lk.showZone, true), chart: oneOf(lk.chart, ["dipDir", "dip", "friction"] as const, "dipDir"),
    },
    geometry: {
      upper: { dip: num(lg.upper?.dip, 0, 0, 90), dipDir: wrap(num(lg.upper?.dipDir, 180)) },
      height: num(lg.height, dL.geometry.height, 0), length: num(lg.length, dL.geometry.length, 0), unitWeight: num(lg.unitWeight, dL.geometry.unitWeight, 0),
      bench: cap(lg.bench),
    },
    materials, other: sanitizeMaterial(l.other),
    run: {
      // files before 2.3.0 always name the basis; "poles" was their default
      basis: oneOf(lr.basis, ["sets", "poles"] as const, "poles"), pairs: oneOf(lr.pairs, ["between", "all"] as const, "between"),
      planar: bool(lr.planar, true), wedge: bool(lr.wedge, true), topple: bool(lr.topple, true), target: num(lr.target, 1.3, 0.1, 10),
      toppleBase: nn(numOrNull(lr.toppleBase, 0, 90)), toppleToe: num(lr.toppleToe, 0), toppleMethod: oneOf(lr.toppleMethod, ["equal-area", "goodman-bray"] as const, "equal-area"),
      // toppleRules (2.1.0–2.5.0) is not read: toppling has one set of rules (D-025)
    },
    loads: {
      water: oneOf(ll.water, ["none", "hb", "custom"] as const, "none"), fill: num(ll.fill, 50, 0, 100), seismicK: num(ll.seismicK, 0, 0, 1),
      seismicTrend: nn(numOrNull(ll.seismicTrend)), force: num(ll.force, 0), forceTrend: num(ll.forceTrend, 0), forcePlunge: num(ll.forcePlunge, 0, -90, 90),
    },
    caps: { volume: cap(lc.volume), weight: cap(lc.weight), trace: cap(lc.trace), persistence: cap(lc.persistence) },
    view: { tab: oneOf(lv.tab, ["net", "3d", "chart"] as const, "net"), intersections: bool(lv.intersections, true), listKind: oneOf(lv.listKind, ["all", "planar", "wedge", "topple"] as const, "all") },
  };

  const t: Any = isObj(j.tunnel) ? j.tunnel : {}, tv: Any = isObj(t.view) ? t.view : {};
  // which joint sets the tunnel uses; the per-set cohesion of files before 2.4.0 is not read,
  // a set's strength is its slope material (D-021)
  const joints: Record<string, { use: boolean }> = {};
  if (isObj(t.joints)) for (const [k, v] of Object.entries(t.joints)) if (isObj(v)) joints[k] = { use: bool(v.use, true) };
  const len = nn(numOrNull(t.length, 1, 10000));
  const section = sanitizeSection(t.section);
  const tunnel = {
    shape: section || t.shape !== "custom" ? oneOf(t.shape, ["horseshoe", "circle", "rectangle", "custom"] as const, dT.shape) : dT.shape,
    width: num(t.width, dT.width, 0.1), height: num(t.height, dT.height, 0.1), section, sectionName: typeof t.sectionName === "string" ? t.sectionName : null,
    trend: wrap(num(t.trend, dT.trend)), plunge: num(t.plunge, 0, 0, 90),
    length: len, unitWeight: num(t.unitWeight, dT.unitWeight, 1, 50), joints, combo: typeof t.combo === "string" ? t.combo : null,
    target: num(t.target, dT.target, 0.1, 10),
    view: { tab: oneOf(tv.tab, ["3d", "section", "net"] as const, dT.view.tab), perimeter: bool(tv.perimeter, true), nearEnd: bool(tv.nearEnd, true),
      farEnd: bool(tv.farEnd, true), unstableOnly: bool(tv.unstableOnly, false) },
  };

  // the report: each flag keeps its default unless the file says true or false
  const r: Any = isObj(j.report) ? j.report : {}, dR = defaultReport();
  const flags = <T extends Record<string, boolean>>(v: unknown, d: T): T => { const o: Any = isObj(v) ? v : {}; return Object.fromEntries(Object.entries(d).map(([k, b]) => [k, bool(o[k], b)])) as T; };
  const text = (v: unknown, max: number) => (typeof v === "string" ? v.slice(0, max) : "");
  const report = {
    title: text(r.title, 200), author: text(r.author, 200), notes: text(r.notes, 4000),
    parts: flags(r.parts, dR.parts), data: flags(r.data, dR.data), slope: flags(r.slope, dR.slope), tunnel: flags(r.tunnel, dR.tunnel),
  };

  const mf: Any = isObj(m.migratedFrom) ? m.migratedFrom : null;
  return {
    format: PROJECT_FORMAT, version: PROJECT_VERSION,
    meta: {
      id: str(m.id, newId()), name: str(m.name, "Untitled project"),
      createdAt: str(m.createdAt, now), savedAt: str(m.savedAt, now),
      savedBy: isObj(m.savedBy) ? { app: str(m.savedBy.app, "?"), version: str(m.savedBy.version, "?") } : null,
      engines: isObj(m.engines) ? Object.fromEntries(Object.entries(m.engines).filter(([, v]) => typeof v === "string")) as Record<string, string> : {},
      migratedFrom: mf ? { from: str(mf.from, "?"), version: num(mf.version, 0), at: str(mf.at, now), notes: Array.isArray(mf.notes) ? mf.notes.filter((n: unknown) => typeof n === "string") : [] } : null,
    },
    units: { ...UNITS },
    data, sets, stereo, slope, tunnel, report,
  };
}

/** A custom tunnel section: at least three finite vertices (x, z), m, consecutive duplicates
 *  and a repeated closing vertex dropped; anything else is no section (null). */
export function sanitizeSection(v: unknown): Array<[number, number]> | null {
  if (!Array.isArray(v)) return null;
  const pts: Array<[number, number]> = [];
  for (const q of v) {
    if (!Array.isArray(q) || q.length < 2) return null;
    const x = Number(q[0]), z = Number(q[1]);
    if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
    const last = pts[pts.length - 1];
    if (!last || last[0] !== x || last[1] !== z) pts.push([x, z]);
  }
  if (pts.length > 1 && pts[0][0] === pts[pts.length - 1][0] && pts[0][1] === pts[pts.length - 1][1]) pts.pop();
  return pts.length >= 3 ? pts : null;
}
